import React from "react";
import { usePreviousFeed, oldMarketAt, oddsChangeClass } from "../betTableSuperOver2/oddsChange";
import { formatBook } from "../../../utils/formatOdds";

/**
 * BetTableSuper3 – market table of casino/superover3 (Mini SuperOver), reference markup/logic
 * (allpanel9 admin SuperOver3 page: Market2 / Market6 / Market8 + Market7):
 *   .market-container >
 *     dtype 2 → .market-2 (Bookmaker, Back/Lay)                       #market{i}
 *     dtype 6 → .market-6 (Fancy, No/Yes, rows per BACK1/BACK2/BACK3) #market{i}
 *     dtype 7 → .market-6 "Tie" (sections sid 9)                      #market{i+1}
 *               .market-6 Fancy1 (all other sections)                 #market{i}
 *   (markets in sno order, gtype "match" skipped but still counted in the index)
 * - body gets .suspendedfull when the market gscode != 1
 * - a row gets .suspendedtext when the market is open (gscode 1) but the section gscode != 1; data-title = gstatus
 * - a price box gets .no-val when its odds are 0; the size line only shows when size and odds are non-zero
 * - sizes use the reference kval format (300000 -> 300K)
 * Clicking a live price keeps the page's onBetClick behaviour (admin: refresh exposure / bets).
 */

// Reference v-kval directive
const kval = (size) => {
  const n = Number(size);
  return n >= 1000 ? `${parseFloat((n / 1000).toFixed(1))}K` : String(parseFloat(n));
};

const sortBy = (list, key, desc = false) =>
  [...(list || [])].sort((a, b) => ((Number(a?.[key]) || 0) - (Number(b?.[key]) || 0)) * (desc ? -1 : 1));

// Reference filterBy(odds, "BACK1", "oname") (case-insensitive contains), ordered by tno desc
const byName = (item, oname) =>
  sortBy(
    (item?.odds || []).filter((o) => String(o.oname || "").toLowerCase().includes(oname)),
    "tno",
    true,
  );

const ARROW = `${import.meta.env.BASE_URL}img/arrow-down.svg`;

const NO_MARKETS = [];

const BetTableSuper3 = ({ data = NO_MARKETS, onBetClick, exposures = {} }) => {
  // previous feed for the reference odds-change colours (.bg-success / .bg-danger)
  const oldData = usePreviousFeed(data);
  const getExposure = (selectionName) => {
    if (!selectionName) return 0;
    const keys = [
      selectionName,
      selectionName.trim(),
      selectionName.toLowerCase(),
      selectionName.toLowerCase().trim(),
      selectionName.toUpperCase(),
      selectionName.toUpperCase().trim(),
    ];
    for (const key of keys) {
      if (key && exposures[key] !== undefined) return exposures[key];
    }
    return 0;
  };

  const isRowSuspended = (market, item) => Number(market?.gscode) === 1 && Number(item.gscode) !== 1;
  const rowClass = (market, item) => `bet-table-row${isRowSuspended(market, item) ? " suspendedtext" : ""}`;

  // One price box (.bl-box back|lay{extra}); clickable only while the price is live
  const priceBox = (market, item, odd, side, extra, key, change = "") => {
    const odds = Number(odd?.odds) || 0;
    const size = Number(odd?.size) || 0;
    const clickable = odds !== 0 && !isRowSuspended(market, item) && Number(market?.gscode) === 1;
    return (
      <div
        key={key}
        className={`bl-box ${side}${extra}${odds === 0 ? " no-val" : ""}${change}`}
        onClick={clickable ? () => onBetClick?.(odds, item.nat, side, item) : undefined}
      >
        <span className="d-block odds">{odds === 0 ? "—" : odd.odds}</span>{" "}
        {size !== 0 && odds !== 0 ? <span className="d-block">{kval(size)}</span> : null}
      </div>
    );
  };

  const bodyProps = (market, index) => ({
    id: `market${index}`,
    "data-title": market.status,
    className: `bet-table-body collapse show${Number(market.gscode) !== 1 ? " suspendedfull" : ""}`,
  });

  const header = (market, index, title, withLock) => (
    <div className="bet-table-header">
      <div data-toggle="collapse" data-target={`#market${index}`} aria-expanded="true" className="nation-name">
        <span title={title}>
          {withLock && (
            <a href="javascript:void(0)" title="">
              <img src={ARROW} className="mr-1" alt="" />
            </a>
          )}
          {` ${title} `}
        </span>
      </div>
      {withLock && (
        <>
          {" "}
          <div className="float-right">
            <a href="javascript:void(0)" className="btn btn-back">
              Bet Lock
            </a>
          </div>
        </>
      )}
    </div>
  );

  const titleRow = (first, second) => (
    <div className="bet-table-row">
      <div className="text-right nation-name" />{" "}
      <div className={`${first === "No" ? "lay" : "back"} bl-title d-none-mobile`}>{first}</div>{" "}
      <div className={`${second === "Yes" ? "back" : "lay"} bl-title d-none-mobile`}>{second}</div>
    </div>
  );

  const nation = (item, bookmaker) =>
    bookmaker ? (
      <div className="nation-name d-none-mobile">
        <p>{item.nat}</p>{" "}
        <p className="mb-0 float-left" style={{ color: "red" }}>
          {formatBook(getExposure(item.nat))}
        </p>{" "}
        <p className="mb-0 float-right d-none">0</p>
      </div>
    ) : (
      <div className="nation-name d-none-mobile">
        <p>{item.nat}</p>{" "}
        <p className="mb-0" style={{ color: "red" }}>
          {formatBook(getExposure(item.nat))}
        </p>
      </div>
    );

  // reference getClass(odds, otype, tno, sectionIndex) against the same market in the previous feed
  const changeOf = (oldMarket, idx, o, side) => oddsChangeClass(oldMarket, idx, side, o.tno, o.odds);

  // Market2 – Bookmaker
  const renderBookmaker = (market, index, oldMarket) => (
    <div className="market-2">
      <div className="bet-table">
        {header(market, index, market.mname, false)}{" "}
        <div {...bodyProps(market, index)}>
          <div className="bet-table-row">
            <div className="text-right nation-name">
              <span className="max-bet" />
            </div>{" "}
            <div className="back bl-title d-none-mobile">Back</div>{" "}
            <div className="lay bl-title d-none-mobile">Lay</div>
          </div>{" "}
          {sortBy(market.section, "sno").map((item, idx) => (
            <div key={idx} data-title={item.gstatus} className={rowClass(market, item)}>
              {nation(item, true)}{" "}
              {sortBy(item.odds?.filter((o) => o.otype === "back"), "tno", true).map((o, i) =>
                priceBox(market, item, o, "back", Number(o.tno) === 0 ? " back" : ` back${o.tno}`, `b${i}`, changeOf(oldMarket, idx, o, "back")),
              )}{" "}
              {sortBy(item.odds?.filter((o) => o.otype === "lay"), "tno").map((o, i) =>
                priceBox(market, item, o, "lay", Number(o.tno) === 0 ? " lay" : ` lay${o.tno}`, `l${i}`, changeOf(oldMarket, idx, o, "lay")),
              )}
            </div>
          ))}
        </div>
      </div>{" "}
      {market.rem ? <div className="remark">{market.rem}</div> : null}
    </div>
  );

  // Market6 – Fancy (No/Yes; one row per BACK1/BACK2/BACK3 level)
  const renderFancy = (market, index, oldMarket) => (
    <div className="market-6">
      <div className="bet-table">
        {header(market, index, market.mname, false)}{" "}
        <div {...bodyProps(market, index)}>
          {titleRow("No", "Yes")}{" "}
          {sortBy(market.section, "sno").map((item, idx) => (
            <React.Fragment key={idx}>
              <div className="fancy-tripple">
                {[1, 2, 3].map((lvl) =>
                  byName(item, `back${lvl}`).length ? (
                    <div key={lvl} data-title={item.gstatus} className={rowClass(market, item)}>
                      {lvl === 1 ? nation(item, false) : <div className="nation-name d-none-mobile" />}{" "}
                      {byName(item, `lay${lvl}`).map((o, i) =>
                        priceBox(market, item, o, "lay", "", `l${i}`, changeOf(oldMarket, idx, o, "lay")),
                      )}{" "}
                      {byName(item, `back${lvl}`).map((o, i) =>
                        priceBox(market, item, o, "back", "", `b${i}`, changeOf(oldMarket, idx, o, "back")),
                      )}
                    </div>
                  ) : null,
                )}
              </div>{" "}
              {item.rem ? <div className="sec-remark">{item.rem}</div> : null}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  // Market8 ("Tie", sid 9) / Market7 (Fancy1, every other section): Back/Lay + arrow and Bet Lock
  const renderFancy1 = (market, index, tie, oldMarket) => (
    <div className="market-6">
      <div className="bet-table">
        {header(market, index, tie ? "Tie" : market.mname, true)}{" "}
        <div {...bodyProps(market, index)}>
          {titleRow("Back", "Lay")}{" "}
          {sortBy(market.section, "sno").map((item, idx) => (
            <React.Fragment key={idx}>
              {(Number(item.sid) === 9) === tie ? (
                <div className="fancy-tripple">
                  <div data-title={item.gstatus} className={rowClass(market, item)}>
                    {nation(item, false)}{" "}
                    {byName(item, "back1").map((o, i) =>
                      priceBox(market, item, o, "back", "", `b${i}`, changeOf(oldMarket, idx, o, "back")),
                    )}{" "}
                    {byName(item, "lay1").map((o, i) =>
                      priceBox(market, item, o, "lay", "", `l${i}`, changeOf(oldMarket, idx, o, "lay")),
                    )}
                  </div>
                </div>
              ) : null}{" "}
              {item.rem ? <div className="sec-remark">{item.rem}</div> : null}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  const markets = sortBy(data, "sno");
  return (
    <div className="market-container">
      {markets.map((market, i) => {
        if (market.gtype === "match") return null;
        const dtype = Number(market.dtype);
        return (
          <React.Fragment key={market.mid ?? i}>
            {dtype === 2 ? renderBookmaker(market, i, oldMarketAt(oldData, i)) : null}
            {dtype === 6 ? renderFancy(market, i, oldMarketAt(oldData, i)) : null}
            {dtype === 7 ? (
              <>
                {renderFancy1(market, i + 1, true, oldMarketAt(oldData, i))}{" "}
                {renderFancy1(market, i, false, oldMarketAt(oldData, i))}
              </>
            ) : null}{" "}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default BetTableSuper3;
